import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {maxNumberCustomFields} from '../../../../../../../../../../src/helpers/customFieldHelpers';
import {makeMapStateToProps} from '../../../../../../../../../../src/helpers/reselectHelpers';
import {CustomFieldModel} from '../../../../../../../../../../src/models/api/customFieldModel';
import {AppState} from '../../../../../../../../../../src/store/appState';
import {getCustomFieldsForAccountsFlat} from '../../../../../../../../../../src/store/data/customFields/customFieldsSelectors';
import {getIsUserGlobalAdmin} from '../../../../../../../../../../src/store/data/teammates/teammatesSelectors';
import {CustomFieldDropdownWrapper} from './customFieldDropdownWrapper';

/*
 * Interface.
 */

interface CustomFieldDropdownContainerProps {
  selectedCustomFieldId?: number;
  allSelectedCustomFieldIds: ReadonlyArray<number>;
}

/*
 * Selectors.
 */

function getSelectedCustomFieldId(_state: AppState, props: CustomFieldDropdownContainerProps) {
  return props.selectedCustomFieldId;
}

function getAllSelectedCustomFieldIds(_state: AppState, props: CustomFieldDropdownContainerProps) {
  return props.allSelectedCustomFieldIds;
}

function makeGetAccountCustomFields() {
  return createSelector(
    [getCustomFieldsForAccountsFlat, getSelectedCustomFieldId, getAllSelectedCustomFieldIds],
    (customFields, selectedCustomFieldId, allSelectedCustomFieldIds) =>
      customFields
        .filter((customField) =>
          filterCustomField(customField, allSelectedCustomFieldIds, selectedCustomFieldId),
        )
        .sort((field) => (field.id === selectedCustomFieldId ? -1 : 1)),
  );
}

function makeGetSelectedCustomField(getAccountCustomFields: ReturnType<typeof makeGetAccountCustomFields>) {
  return createSelector([getAccountCustomFields, getSelectedCustomFieldId], (customFields, selectedId) => {
    if (!selectedId) {
      return undefined;
    }
    return customFields.find((customField) => customField.id === selectedId);
  });
}

function makeGetIsCustomFieldLimitReached() {
  return createSelector(
    [getCustomFieldsForAccountsFlat],
    (accountCustomFields) => accountCustomFields.length >= maxNumberCustomFields,
  );
}

/*
 * Helpers.
 */

function filterCustomField(
  customField: CustomFieldModel,
  allSelectedCustomFieldIds: ReadonlyArray<number>,
  selectedCustomFieldId?: number,
) {
  // only show the selected field and fields that are not selected by other mappings.
  return customField.id === selectedCustomFieldId || !allSelectedCustomFieldIds.includes(customField.id);
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<CustomFieldDropdownContainerProps>()(() => {
  const getAccountCustomFields = makeGetAccountCustomFields();
  return {
    customFields: getAccountCustomFields,
    selectedCustomField: makeGetSelectedCustomField(getAccountCustomFields),
    isCustomFieldLimitReached: makeGetIsCustomFieldLimitReached(),
    isUserGlobalAdmin: getIsUserGlobalAdmin,
  };
});

export const CustomFieldDropdownContainer = connect(mapStateToProps)(CustomFieldDropdownWrapper);
